import type { InputCustomEvent, InputChangeEventDetail } from '@ionic/react'
import { maskitoTransform } from '@maskito/core'
import { maskitoNumberOptionsGenerator } from '@maskito/kit'
import { useMaskito } from '@maskito/react'
import { ParseKeys } from 'i18next'
import { ComponentProps } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParserI18nMessage } from '~/shared/lib/i18n'
import { isNumber } from '~/shared/lib/utils'
import { IonInput } from '../styled'

type NumberParams = {
  min?: number
  max?: number
  precision?: number
  decimalSeparator?: string
  decimalPseudoSeparators?: string[]
  decimalZeroPadding?: boolean
  thousandSeparator?: string
  prefix?: string
  postfix?: string
  minusSign?: string
}

type NumericInputProps = {
  name: string
  numberParams?: NumberParams
  label: ParseKeys
} & Omit<ComponentProps<typeof IonInput>, '$invalid' | 'label'>

export function NumericInput({
  label,
  name,
  type,
  numberParams,
  children,
  ...inputProps
}: NumericInputProps) {
  const { t } = useTranslation()

  const { parserI18nMessage } = useParserI18nMessage()
  const numberOptions = maskitoNumberOptionsGenerator({
    thousandSeparator: ' ',
    min: 0,
    ...numberParams,
  })

  const numberRef = useMaskito({ options: numberOptions })

  return (
    <Controller
      name={name}
      defaultValue=''
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => {
        return (
          <IonInput
            ref={numberRef}
            onIonInput={(e: InputCustomEvent<InputChangeEventDetail>) => {
              onChange(e.detail.value || '')
            }}
            value={maskitoTransform(
              isNumber(value) ? String(value) : value,
              numberOptions,
            )}
            name={name}
            label={t(label)}
            type={type}
            errorText={parserI18nMessage(
              error?.message || 'validate.required field',
            )}
            $invalid={invalid}
            {...inputProps}
            labelPlacement='floating'
            inputMode='numeric'
          >
            {children}
          </IonInput>
        )
      }}
    />
  )
}
